import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    TableContainer,
    Paper,
    Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { QRCodeCanvas } from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
const useStyles = makeStyles((theme) => ({
    tableContainer: {
        maxHeight: "70vh",
        overflow: "auto",
        borderRadius: 16,
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
    },
    headerCell: {
        fontWeight: 700,
        fontSize: "0.85rem",
        textAlign: "center",
        backgroundColor: theme.palette.grey[50],
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
        borderBottom: `2px solid ${theme.palette.grey[300]}`,
        padding: "16px 8px"
    },
    tableRow: {
        "&:hover": {
            backgroundColor: "rgba(0,0,0,0.02)"
        },
        transition: "background-color 0.2s ease"
    },
    tableCell: {
        padding: "12px 8px",
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        verticalAlign: "middle",
    },
    blurContent: {
        filter: "blur(3px)",
        pointerEvents: "none",
        userSelect: "none",
    },

    loaderOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(255,255,255,0.6)",
        backdropFilter: "blur(2px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2,
    },
}));
interface GeneralisedConfirmationPopupProps {
    isOpen: boolean;
    toggleOpen: () => void;
    confirmedAction: () => Promise<any> | any;
    presentResult?: boolean;
    title?: string;
    message?: string;
    showAsTable?: boolean;
    data: Record<string, any>[] | Record<string, any>;
    headers: Record<string, string>;
    onRemoveItem?: (index: number, row: Record<string, any>) => void;
    total_display?: { label: string; value: string | number };
}

export default function GeneralisedConfirmationPopup({
    isOpen,
    toggleOpen,
    confirmedAction,
    presentResult = false,
    title = "Confirmation",
    message,
    showAsTable = false,
    data,
    headers,
    onRemoveItem,
    total_display,
}: GeneralisedConfirmationPopupProps) {
    const classes = useStyles();
    const [resultUrl, setResultUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setResultUrl(null);
        toggleOpen();
    };

    const handleAgree = async () => {
        setLoading(true);
        try {
            const res = await confirmedAction();
            if (presentResult && typeof res === "string") {
                setResultUrl(res);
            } else if (!presentResult) {
                handleClose();
            }
        } catch (e) {
            console.error("Error in confirmedAction:", e);
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    const dataArray = Array.isArray(data) ? data : [data];

    return (
            <Dialog
                open={isOpen}
                onClose={handleClose}
                aria-labelledby="confirmation-dialog-title"
                maxWidth="md"
                fullWidth
                style={{ padding: "12px" }}
            >
                <div style={{ position: "relative" }}>
        
                    {/* Loader overlay on top */}
                    {loading && (
                        <div className={classes.loaderOverlay}>
                            <CircularProgress />
                        </div>
                    )}
        
                    <div className={loading ? classes.blurContent : ""}>
        
                        {!resultUrl && (
                            <DialogTitle id="confirmation-dialog-title" style={{ position: "relative" }}>
                                {title}
                                    <IconButton
                                    aria-label="close"
                                    onClick={handleClose}
                                    style={{
                                        position: "absolute",
                                        right: 8,
                                        top: 8,
                                        color: "#555",
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                        )}
        
                        <DialogContent>
                            {!resultUrl ? (
                                <>
                                    {message && (
                                        <DialogContentText style={{ marginBottom: "16px", color: "#000" }}>
                                            {message}
        
                                            {total_display && (
                                                <>
                                                    <br /><br />
                                                    <strong style={{ fontWeight: 600 }}>
                                                        {total_display.label}: $ {total_display.value}
                                                    </strong>
                                                    <br /><br />
                                                </>
                                            )}
                                        </DialogContentText>
                                    )}
        
                                    {showAsTable ? (
                                        <TableContainer component={Paper} className={classes.tableContainer}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        {Object.keys(headers).map((key) => (
                                                            <TableCell
                                                                key={key}
                                                                className={classes.headerCell}
                                                                align="center"
                                                            >
                                                                {headers[key]}
                                                            </TableCell>
                                                        ))}
                                                        {onRemoveItem && <TableCell />}
                                                    </TableRow>
                                                </TableHead>
        
                                                <TableBody>
                                                    {dataArray.map((row, index) => (
                                                        <TableRow key={index}>
                                                            {Object.keys(headers).map((key) => (
                                                                <TableCell
                                                                    key={key}
                                                                    className={classes.tableCell}
                                                                    align="center"
                                                                >
                                                                    {String(row[key] ?? "")}
                                                                </TableCell>
                                                            ))}
        
                                                            {onRemoveItem && (
                                                                <TableCell align="center">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => onRemoveItem(index, row)}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <DialogTitle>
                                        {title}
                                        <IconButton
                                            onClick={handleClose}
                                            style={{
                                                position: "absolute",
                                                right: 12,
                                                top: 12,
                                                color: "#666",
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </DialogTitle>
        
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
                                            <Typography variant="body2" style={{ color: "#888", marginBottom: "8px" }}>
                                                Scan QR Code to proceed
                                            </Typography>
        
                                            <QRCodeCanvas
                                                value={resultUrl}
                                                size={180}
                                                style={{ border: "4px solid #ddd", padding: "8px" }}
                                            />
                                        </Grid>
        
                                        <Grid item xs={12} style={{ textAlign: "center" }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => window.open(resultUrl, "_blank")}
                                                style={{
                                                    backgroundColor: "#007bff",
                                                    color: "white",
                                                    padding: "6px 12px",
                                                    borderRadius: "5px",
                                                    fontSize: "15px",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                Pay Online
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </DialogContent>
        
                        <DialogActions style={{ justifyContent: "center", paddingBottom: "16px" }}>
                            {!resultUrl && (
                                <Button
                                    onClick={handleAgree}
                                    color="primary"
                                    variant="contained"
                                    disabled={loading || dataArray.length <= 1}
                                >
                                    {loading ? "Processing..." : "Proceed"}
                                </Button>
                            )}
                        </DialogActions>
        
                    </div>
                </div>
            </Dialog>        
    );
}
